import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import LocationInput from "./LocationInput";
import AddImageButton from "./AddImageButton";
import DeleteIcon from "@mui/icons-material/Delete";
import FormControl from "@mui/material/FormControl";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import FlagIcon from "@mui/icons-material/Flag";
import EditIcon from "@mui/icons-material/Edit";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Box from "@mui/material/Box";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import { BootstrapTooltip, filterObjectsUptoCategory, timestamp } from "../../utils/services";
import { Autocomplete, Fade, Paper, Popper } from "@mui/material";
import FroalaEditorComponent from "../froalaHtmlEditor/FroalaEditorComponent";
import { filterSelectTag } from "./CommentLibraryHelper";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { resolveCommentRatingsFromComment, resolveRemovedDefaultRatingId, stripDefaultRating } from "./lineRatingUtils";

const froalaConfig = {
  imagePaste: false,
  spellCheck: true,
  heightMin: 140,
  heightMax: 360,
  linkAutoPrefix: 'https://',
  linkAlwaysBlank: true,
  toolbarButtons: {
    moreText: { buttons: ['bold', 'italic', 'underline', '|', 'fontFamily', 'fontSize', 'textColor', 'backgroundColor'], align: 'left', buttonsVisible: 7 },
    moreParagraph: { buttons: ['|', 'alignLeft', 'alignCenter', 'alignRight', 'formatOLSimple', 'formatUL'], align: 'left', buttonsVisible: 8 },
    moreRich: { buttons: ['|', "insertLink", "outdent", "indent"], align: "left", buttonsVisible: 1 },
    moreMisc: { buttons: ['undo', 'redo', 'selectAll', 'html', 'clearFormatting'], align: 'right', buttonsVisible: 2 }
  },
  toolbarButtonsXS: [['fontFamily', 'fontSize', 'bold', 'italic', 'underline', '|'], ['undo', 'redo']],
  pluginsEnabled: ["wordPaste", "spellChecker", "codeBeautifier", "fontFamily", "fontSize", "colors", "align", "lists", 'paragraphFormat', "link"],
  events: {
    'initialized': function () {
      try {
        if (this.$wp && this.$wp[0]) {
          this.$wp[0].style.height = '340px';
        }
      } catch (e) {}
    }
  }
};

const CommentsSection = (props) => {
  const { commentData, cmtRating, categoryId, lineId, cmtLocHolder, AllCategoryList, setAllCategoryList, updateTemplate, MediaHolder, commentId, isCommentDialogOpen } = props;
  const dispatch = useDispatch();
  const categories = useSelector((state) => state.templates)?.categoryList;
  const templateList = useSelector((state) => state?.templates?.templateList);
  const storeData = useSelector((state) => state.commentLibrary);
  const [state, setState] = useState({
    editLocation: false,
    editText: "",
    isExpanded: true,
    // anchorEl: null,
  });
  const [mediaState, setMediaState] = useState({
    editMedia: false,
    isMediaExpanded: true,
    // anchorEl: null,
  });
  const { editLocation, editText, isExpanded } = state;
  const { editMedia, isMediaExpanded } = mediaState;
  const [placement, setPlacement] = React.useState();
  const [mediaPlacement, setMediaPlacement] = React.useState();
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorMediaEl, setAnchorMediaEl] = useState(null);
  const commentRef = useRef(null);
  const handleClick = (newPlacement) => (event) => {
    setAnchorEl(event.currentTarget);
    setPlacement(newPlacement);
  };
  const handleMediaClick = (newPlacement) => (event) => {
    setAnchorMediaEl(event.currentTarget);
    setMediaPlacement(newPlacement);
  };

  const handleClickAway = () => {
    setAnchorEl(null);
  };
  const handleMediaClickAway = () => {
    setAnchorMediaEl(null);
  };

  const open = Boolean(anchorEl);
  const mediaOpen = Boolean(anchorMediaEl);
  // The template default rating only applies to lines/comments while global ratings are enabled.
  const isGlobalRatingEnabled = !!templateList?.is_global_rating_enabled;
  const [ratingValue, setRatingValue] = useState(() =>
    resolveCommentRatingsFromComment(commentData, AllCategoryList?.template?.rating_data, isGlobalRatingEnabled)
  );
  const [editorContent, setEditorContent] = useState({ innerHTML: commentData?.recommendation || commentData?.comment_text || "", dropdownList: commentData?.comment_dropdowns || [] });
  const locationData = AllCategoryList?.template?.location_data;
  const imageData = commentData.comment_input_controls.length > 0 && commentData?.comment_input_controls?.find((item) => item.type === "Media")?.data;
  const location = commentData.comment_input_controls.length > 0 && commentData?.comment_input_controls?.find((item) => item.type === "Location");
  const media = commentData.comment_input_controls.length > 0 && commentData?.comment_input_controls?.find((item) => item.type === "Media");
  const ratingsMap = AllCategoryList?.template?.rating_data;
  const templateGuid = AllCategoryList.template.guid;
  let baseUrl = AllCategoryList.bucket_url;
  const tempMedia = MediaHolder[0];
  let lineIndex = null;
  let commentIndex = null;
  const catList = AllCategoryList?.template?.categories;
  const categoriesDataIndex = catList.findIndex((cat) => cat.category_id === categoryId);
  if (categoriesDataIndex !== -1) {
    lineIndex = catList[categoriesDataIndex].lines.findIndex((line) => line.line_id === lineId);
    commentIndex = catList[categoriesDataIndex].lines[lineIndex].comments.findIndex((comment) => comment.comment_id === commentData.comment_id);
    if (commentIndex !== -1) {
      // Ratings are written by handleChangeRating, not from here: `ratingValue` holds the resolved
      // list (selection + template default) and is a render behind the template's ratings, so
      // writing it back turned a default that had just been replaced into a real selection.
      catList[categoriesDataIndex].lines[lineIndex].comments[commentIndex].recommendation = filterSelectTag(editorContent?.innerHTML);
      catList[categoriesDataIndex].lines[lineIndex].comments[commentIndex].updated_at = timestamp();
    }
  }

  const deleteImage = (pathId) => {
    const catList = AllCategoryList?.template?.categories;
    const categoriesDataIndex = catList.findIndex((cat) => cat.category_id === categoryId);
    const lineIndex = catList[categoriesDataIndex].lines.findIndex((line) => line.line_id === lineId);
    commentIndex = catList[categoriesDataIndex].lines[lineIndex].comments.findIndex((comment) => comment.comment_id === commentId);
    let selectd = catList[categoriesDataIndex].lines[lineIndex].comments[commentIndex].comment_input_controls;
    catList[categoriesDataIndex].lines[lineIndex].comments[commentIndex].updated_at = timestamp();
    selectd.map((sel) => {
      if (sel.type === "Media") {
        sel.updated_at = timestamp();
        sel.data.map((med) => {
          if (med.id === pathId) {
            med.is_deleted = 1;
            med.updated_at = timestamp();
            filterObjectsUptoCategory(catList, categoryId, dispatch, categories);
            updateTemplate("noToast");
          }
        });
      }
    });
  };

  const updateLocation = (type, remove) => {
    if (type === "edit") {
      setState((prevState) => ({ ...prevState, editLocation: false, }));
      setAnchorEl(null);
    } else {
      setState((prevState) => ({ ...prevState, isExpanded: remove ? false : true, }));
      setAnchorEl(null);
    }
    const catList = AllCategoryList?.template?.categories;
    const categoriesDataIndex = catList.findIndex((cat) => cat.category_id === categoryId);
    const lineIndex = catList[categoriesDataIndex].lines.findIndex((line) => line.line_id === lineId);
    commentIndex = catList[categoriesDataIndex].lines[lineIndex].comments.findIndex((comment) => comment.comment_id === commentId);
    let selectd = catList[categoriesDataIndex].lines[lineIndex].comments[commentIndex].comment_input_controls;
    const locationData = selectd.find((obj) => obj.type === "Location");
    if (locationData) {
      selectd.map((sel) => {
        if (sel.type === "Location") {
          if (type === "remove") {
            sel.is_disabled = remove;
            sel.updated_at = timestamp();
          } else {
            sel.prompt = editText;
            sel.is_manual_promt_edit = 1;
            sel.updated_at = timestamp();
          }
          filterObjectsUptoCategory(catList, categoryId, dispatch, categories);
          updateTemplate("noToast");
        }
      });
    } else {
      let newData = cmtLocHolder;
      if (type === "remove") newData[0].is_disabled = remove;
      else {
        newData[0].prompt = editText;
        newData[0].is_manual_promt_edit = 1;
        newData[0].updated_at = timestamp();
      }
      selectd.push(newData[0]);
      filterObjectsUptoCategory(catList, categoryId, dispatch, categories);
      updateTemplate("noToast");
    }
  };

  const updateMedia = (type, remove) => {
    setMediaState((prevState) => ({
      ...prevState,
      isMediaExpanded: remove ? false : true,
    }));
    setAnchorMediaEl(null);
    const categoryList = AllCategoryList?.template?.categories;
    const categoriesDataIndex = categoryList.findIndex((cat) => cat.category_id === categoryId);
    const lineIndex = categoryList[categoriesDataIndex].lines.findIndex((line) => line.line_id === lineId);
    commentIndex = categoryList[categoriesDataIndex].lines[lineIndex].comments.findIndex((comment) => comment.comment_id === commentId);
    let selected = categoryList[categoriesDataIndex].lines[lineIndex].comments[commentIndex].comment_input_controls;
    const MediaData = selected.find((obj) => obj.type === "Media");
    if (MediaData) {
      selected.map((sel) => {
        if (sel.type === "Media") {
          if (type === "remove") {
            sel.is_disabled = remove;
            sel.updated_at = timestamp();
          } else {
            sel.prompt = editText;
            sel.is_manual_promt_edit = 1;
            sel.updated_at = timestamp();
          }
          filterObjectsUptoCategory(catList, categoryId, dispatch, categories);
          updateTemplate("noToast");
        }
      });
    } else {
      let mediaData = MediaHolder;
      if (type === "remove") mediaData[0].is_disabled = remove;
      else {
        mediaData[0].prompt = editText;
        mediaData[0].is_manual_promt_edit = 1;
        mediaData[0].updated_at = timestamp();
      }
      selected.push(mediaData[0]);
      filterObjectsUptoCategory(catList, categoryId, dispatch, categories);
      updateTemplate("noToast");
    }
  };

  const handleChangeRating = (e, value) => {
    setRatingValue(value);
    const values = Array.isArray(value) ? value : [];
    // The template default is shown on every comment without being stored on it, so it is dropped
    // before the comment is updated — resolveCommentRatingsFromComment re-applies it for display.
    const storedValues = stripDefaultRating(values, ratingsMap, isGlobalRatingEnabled);
    const catsList = AllCategoryList?.template?.categories;
    const catIndex = catsList?.findIndex((cat) => cat.category_id === categoryId);
    if (catIndex !== undefined && catIndex !== -1) {
      const lnIndex = catsList[catIndex]?.lines?.findIndex((line) => line.line_id === lineId);
      const cmtIndex = lnIndex !== -1
        ? catsList[catIndex]?.lines[lnIndex]?.comments?.findIndex((comment) => comment.comment_id === commentData.comment_id)
        : -1;
      const comment = cmtIndex !== -1 ? catsList[catIndex].lines[lnIndex].comments[cmtIndex] : null;
      if (comment) {
        if (!Array.isArray(comment.comment_input_controls)) comment.comment_input_controls = [];
        const ratingCtrl = comment.comment_input_controls.find((item) => item.type === "Rating");
        if (ratingCtrl) {
          ratingCtrl.data = storedValues;
          ratingCtrl.updated_at = timestamp();
        } else {
          comment.comment_input_controls.push({ ...cmtRating[0], data: storedValues, updated_at: timestamp() });
        }
        comment.rating = storedValues;
        comment.defaultRating_id = storedValues.length > 0 ? Number(storedValues[0].Id) : null;
        comment.removed_default_rating_id = resolveRemovedDefaultRatingId(values, ratingsMap, isGlobalRatingEnabled);
        comment.updated_at = timestamp();
      }
    }
    setTimeout(() => {
      filterObjectsUptoCategory(catList, categoryId, dispatch, categories);
      updateTemplate("noToast");
    }, 800);
  };

  const storedRatingData = Array.isArray(commentData?.comment_input_controls)
    ? commentData.comment_input_controls.find((item) => item.type === "Rating")?.data
    : undefined;
  useEffect(() => {
    setRatingValue(resolveCommentRatingsFromComment(commentData, ratingsMap, isGlobalRatingEnabled));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentData?.comment_id, commentData?.removed_default_rating_id, storedRatingData, ratingsMap, isGlobalRatingEnabled]);

  const inputRef = useRef(null);

  const handleCommentChange = (id, text, dropdowns) => {
    const catList = AllCategoryList?.template?.categories;
    const categoryIndex = catList.findIndex((cat) => cat.category_id === storeData?.showClModal?.selectedCategory?.id);
    let lineIndex = catList[categoryIndex]?.lines?.findIndex((line) => line?.line_id === lineId);
    let commentIndex = catList[categoryIndex]?.lines[lineIndex]?.comments.findIndex((comment) => comment?.comment_id === commentId);
    catList[categoryIndex].lines[lineIndex].comments[commentIndex] = { ...catList[categoryIndex]?.lines[lineIndex]?.comments[commentIndex], updated_at: new Date().toISOString() };
    catList[categoryIndex].lines[lineIndex].comments[commentIndex].recommendation = filterSelectTag(text);
    catList[categoryIndex].lines[lineIndex].comments[commentIndex].comment_dropdowns = dropdowns || editorContent.dropdownList;
    setEditorContent((prevState) => ({ ...prevState, innerHTML: text }));
    filterObjectsUptoCategory(catList, storeData?.showClModal?.selectedCategory?.id, dispatch, categories);
    updateTemplate("noToast");
    setAllCategoryList((prev) => {
      const newCategoryList = { ...AllCategoryList };
      newCategoryList.template.categories = catList;
      newCategoryList.template.categories.updated_at = timestamp();
      return newCategoryList;
    });
  };

  const handleChangeDropdown = (event, dropdownUuid) => {
    let selectedValue = event?.target?.value || event;
    if (selectedValue?.includes && selectedValue.includes("---")) selectedValue = null;
    const catList = AllCategoryList?.template?.categories;
    const categoryIndex = catList.findIndex((cat) => cat.category_id === storeData?.showClModal?.selectedCategory?.id);
    let lineIndex = catList[categoryIndex]?.lines?.findIndex((line) => line?.line_id === lineId);
    let commentIndex = catList[categoryIndex]?.lines[lineIndex]?.comments.findIndex((comment) => comment?.comment_id === commentId);
    let commentDropdownIndex = catList[categoryIndex]?.lines[lineIndex].comments[commentIndex].comment_dropdowns.findIndex((dropdown) => dropdown.dropdown_uuid === dropdownUuid);
    catList[categoryIndex].lines[lineIndex].comments[commentIndex] = { ...catList[categoryIndex]?.lines[lineIndex].comments[commentIndex], updated_at: new Date().toISOString() };
    catList[categoryIndex].lines[lineIndex].comments[commentIndex].comment_dropdowns[commentDropdownIndex] = {
      ...catList[categoryIndex].lines[lineIndex].comments[commentIndex].comment_dropdowns[commentDropdownIndex],
      selected_value: selectedValue,
    };
    setEditorContent((prevState) => ({ ...prevState, dropdownList: catList[categoryIndex].lines[lineIndex].comments[commentIndex].comment_dropdowns }));
    filterObjectsUptoCategory(catList, storeData?.showClModal?.selectedCategory?.id, dispatch, categories);
    updateTemplate("noToast");
    setAllCategoryList((prev) => {
      const newCategoryList = { ...AllCategoryList };
      newCategoryList.template.categories = catList;
      newCategoryList.template.categories.updated_at = timestamp();
      return newCategoryList;
    });
  };

  const rebindDropdownListeners = () => {
    let commentDropElements = document.querySelectorAll(".comment-dropdown");
    commentDropElements.forEach((element) => {
      const commentUUId = element.getAttribute("comment_uuid");
      const commentDisabled = element.getAttribute("disabled");
      if (element) {
        element.onclick = (event) => event.stopPropagation()
        element.onchange = (event) => handleChangeDropdown(event, element.id, "comment", commentUUId, commentDisabled);
      }
    });
  };

  useEffect(() => {
    const commentEditorData = commentRef.current;
    commentEditorData?.events?.on("contentChanged", () => {
      rebindDropdownListeners();
    });
    rebindDropdownListeners();
    return () => {
      commentEditorData?.events?.off("contentChanged");
    };
  }, [AllCategoryList]);

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const resolveColor = (item, ratingOptions = []) => {
    if (!item) return "#CED2DA";
    const live = ratingOptions.find((o) => o.Id === item.Id);
    const c = (live || item).color ?? (live || item).Color ?? (live || item).colour;
    if (typeof c === "string") return c;
    if (c && typeof c === "object") {
      return c.hex ?? c.code ?? c.value ?? c.name ?? "#CED2DA";
    }
    return "#CED2DA";
  };

  const isTruthy = (v) => {
    if (v == null) return false;
    if (typeof v === "boolean") return v;
    if (typeof v === "number") return v !== 0;
    if (typeof v === "string") return ["1", "true", "yes", "y"].includes(v.toLowerCase());
    return false;
  };

  const resolveIncludeInSummary = (item, ratingOptions = []) => {
    if (!item) return false;
    const live =
      ratingOptions.find((o) => o.Id === item.Id) ??
      ratingOptions.find((o) => o.Name === item.Name && o.Color === item.Color) ??
      item;
    let v =
      parseInt(live.include_in_summary) ??
      live.includeInSummary ??
      live.is_flagged ??
      live.isFlagged ??
      live.flag ??
      live.include ??
      parseInt(live?.summary?.include_in_summary);
    if (v && typeof v === "object") {
      v = v.value ?? v.enabled ?? v.bool ?? v.checked ?? v.flag;
    }
    return isTruthy(v);
  };

  const resolveName = (item, ratingOptions = []) => {
    if (!item) return "";
    const live = ratingOptions.find((o) => o.Id === item.Id) || item;
    return live?.Name ?? live?.name ?? "";
  };

  return (
    <Grid>
      <div className="comment-section-wrapper">
        <p className="line-sub-head">Text</p>
        <FroalaEditorComponent
          editor={commentRef}
          id={"nonNarrativeComment"}
          onChange={handleCommentChange}
          editorData={editorContent}
          onBlur={handleCommentChange}
          froalaConfig={froalaConfig}
          commentData={commentData}
          type="edit"
          setEditorContent={setEditorContent}
          handleChangeDropdown={handleChangeDropdown}
          AllCategoryList={AllCategoryList}
          filterTagList={storeData?.clTagList}
          newOpen={isCommentDialogOpen}
        />
      </div>
      <div className="comment-section-wrapper">
        <p className="line-sub-head">Select Default rating</p>
        <Grid item xs={12} sm={7} md={6} style={{ marginTop: "6px", marginBottom: "20px" }}>
          {/* <FormControl fullWidth className="field-reset template-rating-select">
            <Select labelId="rating-select-label" id="templ(ate-rating-select" size="small" displayEmpty value={ratingValue} onChange={handleChangeRating}>
              <MenuItem value={null}>
                <div className="isFlexCentreAlign rating_section">
                  <div className="rating_square"></div>
                  <span>Select rating</span>
                </div>
              </MenuItem>
              {ratingsMap?.map((rating, index) => {
                return (
                  <MenuItem key={rating.Id} value={rating.Id}>
                    <div className="isFlexCentreAlign rating_section">
                      <div className="rating_square" style={{ background: `${rating.Color}` }}>
                        {(rating.include_in_summary === "1" || rating.include_in_summary === 1) && <FlagIcon />}
                      </div>
                      <span>{rating.Name}</span>
                    </div>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl> */}
          <FormControl fullWidth >
            <Autocomplete
              multiple
              size="small"
              limitTags={2}
              options={ratingsMap || []}
              value={Array.isArray(ratingValue) ? ratingValue : []}
              onChange={(e, value) => handleChangeRating(e, value)}
              isOptionEqualToValue={(option, value) => ((option.Id === value.Id) || (option.Name === value.Name && option.Color === value.Color && parseInt(option.include_in_summary) === parseInt(value.include_in_summary)))}
              disableCloseOnSelect
              getOptionLabel={(option) => option?.name || option?.Name}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const tagProps = getTagProps({ index });
                  const color = resolveColor(option, ratingsMap);
                  const flagged = resolveIncludeInSummary(option, ratingsMap);
                  return (
                    <Box
                      key={option?.id}
                      {...tagProps}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        px: 0.5,
                        py: 0,
                        pl: 1,
                        borderRadius: "16px",
                        backgroundColor: "#FFF",
                        border: "1px solid #CED2DA",
                        color: "#637083",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      <span
                        style={{ height: 15, width: 15, backgroundColor: color, borderRadius: 5, }}
                      >
                        {flagged ? <FlagIcon sx={{ color: "white", fontSize: 15 }} /> : null}
                      </span>
                      <span
                        style={{
                          maxWidth: 140,
                          color: "#637083",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          marginTop: 2,
                        }}
                      >
                        {resolveName(option, ratingsMap)}
                      </span>
                      <IconButton size="small" onClick={tagProps.onDelete} sx={{ height: 20, width: 20, ml: 0.25, color: "#fff", backgroundColor: "#97A1AF" }}>
                        <CloseOutlinedIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  );
                })
              }
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option?.id}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    checked={(Array.isArray(ratingValue) ? ratingValue : [])?.some((tag) => (option.Id === tag.Id) || (option.Name === tag.Name && option.Color === tag.Color && parseInt(option.include_in_summary) === parseInt(tag.include_in_summary)))}
                  />
                  <Box
                    sx={{
                      width: 15,
                      height: 15,
                      backgroundColor: option?.color || option?.Color,
                      borderRadius: "2px",
                      marginRight: 1,
                    }}
                  >
                    {parseInt(option?.include_in_summary) === 1 ? <FlagIcon sx={{ color: "white", fontSize: 15, mb: 0.3 }} /> : ""}
                  </Box>
                  {option?.Name || option?.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" placeholder={(Array.isArray(ratingValue) ? ratingValue : []).length === 0 ? "No ratings selected" : ""} />
              )}
            />
          </FormControl>
        </Grid>
      </div >
        <Popper open={open} anchorEl={anchorEl} placement={placement} transition>
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Paper>
                <ClickAwayListener onClickAway={handleClickAway}>
                  <div style={{ padding: "10px" }}>
                    {/* Texas templates have fixed form element titles — everywhere else the
                        title stays editable. */}
                    {AllCategoryList?.template?.is_texas !== 1 && (
                      <MenuItem
                        style={{ padding: "8px" }}
                        onClick={() => {
                          setState((prevState) => ({
                            ...prevState,
                            editLocation: true,
                            editText: location?.prompt || "DEFAULT LOCATION",
                          }));
                          setAnchorEl(null);
                        }}>
                        <EditIcon className="editDescIcon" />
                        <Typography className="editDescText">Edit Title</Typography>
                      </MenuItem>
                    )}
                    <MenuItem style={{ padding: "8px" }} onClick={() => updateLocation("remove", 1)}>
                      <RemoveCircleOutlineRoundedIcon className="editDescIcon" />
                      <Typography className="editDescText">Remove Form Element</Typography>
                    </MenuItem>
                  </div>
                </ClickAwayListener>
              </Paper>
            </Fade>
          )}
        </Popper>
        <Popper open={mediaOpen} anchorEl={anchorMediaEl} placement={mediaPlacement} transition>
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Paper>
                <ClickAwayListener onClickAway={handleMediaClickAway}>
                  <div style={{ padding: "10px" }}>
                    <MenuItem style={{ padding: "8px" }} onClick={() => updateMedia("remove", 1)}>
                      <RemoveCircleOutlineRoundedIcon className="editDescIcon" />
                      <Typography className="editDescText">Remove Form Element</Typography>
                    </MenuItem>
                  </div>
                </ClickAwayListener>
              </Paper>
            </Fade>
          )}
        </Popper>
      {AllCategoryList?.template?.is_hip_template !== 1 ? <Grid container display={"flex"} mb={1}>
        <Grid item xs={12}>
          <Accordion
            expanded={location?.is_disabled ? false : isExpanded}
            className="accordionBox accordionSumFocus"
            onChange={() =>
              setState((prevState) => ({
                ...prevState,
                isExpanded: location?.is_disabled ? false : !isExpanded,
              }))
            }>
            <AccordionSummary className="accordionSumDesc accordionSumFocus">
              <Grid container gap={0.7}>
                <Grid item xs={location?.is_disabled ? 11.2 : 9} sm={location?.is_disabled ? 11.3 : 10.6} md={location?.is_disabled ? 11.2 : 9.3} lg={location?.is_disabled ? 11.3 : 10.6}>
                  {editLocation ? (
                    <ClickAwayListener mouseEvent="onMouseDown" touchEvent="onTouchStart" onClickAway={() => updateLocation("edit")}>
                      <input
                        type="text"
                        name="title"
                        value={editText}
                        onKeyDown={(e) => e.keyCode === 13 && updateLocation("edit")}
                        onFocus={(event) => event.target.select()}
                        ref={inputRef}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) =>
                          setState((prevState) => ({
                            ...prevState,
                            editText: e.target.value,
                          }))
                        }
                        className="text"
                      />
                    </ClickAwayListener>
                  ) : (
                    <Grid container display={"flex"} gap={0.1}>
                      <Grid
                        item
                        xs={!location?.is_disabled ? 12 : 2.4}
                        sm={!location?.is_disabled ? 11 : 2.4}
                        md={!location?.is_disabled ? 12 : 2.4}
                        lg={!location?.is_disabled ? 11 : 2.4}
                        display={"flex"}>
                        <Typography className={location?.is_disabled ? "textDisable" : "text"}>{location?.is_manual_promt_edit === 1 ? location?.prompt : `DEFAULT LOCATION`}</Typography>
                      </Grid>
                      {location?.is_disabled ? (
                        <Grid item xs={12} sm={9.5} md={12} lg={9.5} display={"flex"} alignItems={"center"}>
                          <Typography className="titleDisable">{`*This form element has been removed and will not be published.`}</Typography>
                        </Grid>
                      ) : null}
                    </Grid>
                  )}
                </Grid>
                {location?.is_disabled ? (
                  <Grid item xs={0.5} display={"flex"} justifyContent={"flex-end"} alignItems={"center"}>
                    <BootstrapTooltip title={<h1>Add form element</h1>}>
                      <AddCircleOutlineRoundedIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          updateLocation("remove", 0);
                        }}
                      />
                    </BootstrapTooltip>
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={1} sm={0.5} md={1} lg={0.5} display={"flex"} justifyContent={"flex-end"} alignItems={"center"}>
                      {!isExpanded ? (
                        <ExpandMoreIcon
                          onClick={(event) => {
                            event.stopPropagation();
                            setState((prevState) => ({
                              ...prevState,
                              isExpanded: !isExpanded,
                            }));
                          }}
                        />
                      ) : (
                        <ExpandLessIcon
                          onClick={(event) => {
                            event.stopPropagation();
                            setState((prevState) => ({
                              ...prevState,
                              isExpanded: !isExpanded,
                            }));
                          }}
                        />
                      )}
                    </Grid>
                      <Grid item xs={1} sm={0.5} md={1} lg={0.5} display={"flex"} justifyContent={"flex-end"} alignItems={"center"}>
                        <MoreHorizIcon
                          onClick={(event) => {
                            event.stopPropagation();
                            setAnchorEl(event.currentTarget);
                            handleClick("bottom-end")(event);
                          }}
                        />
                      </Grid>
                  </>
                )}
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <LocationInput
                cmtLocHolder={cmtLocHolder}
                lineName="coverings"
                categoryId={categoryId}
                lineId={lineId}
                locationData={locationData}
                AllCategoryList={AllCategoryList}
                action="comment"
                locationValue={commentData.default_location}
                commentId={commentId}
                updateTemplate={updateTemplate}
              />
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid> : null
      }
      <Grid container display={"flex"} mb={1}>
        <Grid item xs={12}>
          <Accordion
            expanded={media?.is_disabled ? false : isMediaExpanded}
            className="accordionBox accordionSumFocus"
            onChange={() =>
              setMediaState((prevState) => ({
                ...prevState,
                isMediaExpanded: media?.is_disabled ? false : !isMediaExpanded,
              }))
            }>
            <AccordionSummary className="accordionSumDesc accordionSumFocus">
              <Grid container gap={0.7}>
                <Grid item xs={media?.is_disabled ? 11.2 : 9} sm={media?.is_disabled ? 11.3 : 10.6} md={media?.is_disabled ? 11.2 : 9.3} lg={media?.is_disabled ? 11.3 : 10.6}>
                  {editMedia ? (
                    <ClickAwayListener
                      mouseEvent="onMouseDown"
                      touchEvent="onTouchStart"
                    // onClickAway={() => updateLocation("edit")}
                    >
                      <input
                        type="text"
                        name="title"
                        value={editText}
                        onKeyDown={(e) => e.keyCode === 13 && updateLocation("edit")}
                        onFocus={(event) => event.target.select()}
                        ref={inputRef}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) =>
                          setMediaState((prevState) => ({
                            ...prevState,
                            // editText: e.target.value,
                          }))
                        }
                        className="text"
                      />
                    </ClickAwayListener>
                  ) : (
                    <Grid container display={"flex"} gap={0.1}>
                      <Grid item xs={!media?.is_disabled ? 12 : 2.4} sm={!media?.is_disabled ? 11 : 2.4} md={!media?.is_disabled ? 12 : 2.4} lg={!media?.is_disabled ? 11 : 2.4} display={"flex"}>
                        <Typography className={media?.is_disabled ? "textDisable" : "text"}>{media?.is_manual_promt_edit === 1 ? media?.prompt : `MEDIA`}</Typography>
                      </Grid>
                      {media?.is_disabled ? (
                        <Grid item xs={12} sm={9.5} md={12} lg={9.5} display={"flex"} alignItems={"center"}>
                          <Typography className="titleDisable">{`*This form element has been removed and will not be published.`}</Typography>
                        </Grid>
                      ) : null}
                    </Grid>
                  )}
                </Grid>
                {media?.is_disabled ? (
                  <Grid item xs={0.5} display={"flex"} justifyContent={"flex-end"} alignItems={"center"}>
                    <BootstrapTooltip title={<h1>Add form element</h1>}>
                      <AddCircleOutlineRoundedIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          updateMedia("remove", 0);
                        }}
                      />
                    </BootstrapTooltip>
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={1} sm={0.5} md={1} lg={0.5} display={"flex"} justifyContent={"flex-end"} alignItems={"center"}>
                      {!isMediaExpanded ? (
                        <ExpandMoreIcon
                          onClick={(event) => {
                            event.stopPropagation();
                            setMediaState((prevState) => ({
                              ...prevState,
                              isMediaExpanded: !isMediaExpanded,
                            }));
                          }}
                        />
                      ) : (
                        <ExpandLessIcon
                          onClick={(event) => {
                            event.stopPropagation();
                            setMediaState((prevState) => ({
                              ...prevState,
                              isMediaExpanded: !isMediaExpanded,
                            }));
                          }}
                        />
                      )}
                    </Grid>
                      <Grid item xs={1} sm={0.5} md={1} lg={0.5} display={"flex"} justifyContent={"flex-end"} alignItems={"center"}>
                        <MoreHorizIcon
                          onClick={(event) => {
                            event.stopPropagation();
                            setAnchorMediaEl(event.currentTarget);
                            handleMediaClick("bottom-end")(event);
                          }}
                        />
                      </Grid>
                  </>
                )}
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <Grid sx={{ mt: "20px" }} container className="template-image-wrapper">
                {imageData?.length > 0 &&
                  imageData?.map((img, index) => {
                    const url = baseUrl.concat(img?.onlineUrl);
                    return img.is_deleted !== 1 ? (
                      <div>
                        <img alt="img" src={url} key={index} />
                        <DeleteIcon onClick={() => deleteImage(img.id)} className="delete-wrappers" />
                      </div>
                    ) : (
                      ""
                    );
                  })}
              </Grid>
              <AddImageButton
                templateGuid={templateGuid}
                isPlaceHolder={false}
                tempMedia={tempMedia}
                commentId={commentData.comment_id}
                categoryId={categoryId}
                lineId={lineId}
                from="comments"
                updateTemplate={updateTemplate}
                lineInputControlId={commentId}
                AllCategoryList={AllCategoryList}
              />
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    </Grid >
  );
};

export default CommentsSection;
